import Backbone from 'backbone'
import template from './transformations.hbs'
import _ from 'lodash'
import $ from 'jquery'
import TransformationForm from './transformation-form'
import FormView from 'scoreboard/components/form-view'
import appDisplay from 'scoreboard/components/app-display'
import renderTransformationThen from './render-transformation-then'
import ifDisplay from './if-display.hbs'
import thenDisplay from './then-display.hbs'
import transformationButtons from './transformation-buttons.hbs'
import enums from 'scoreboard/components/enums'
import filterText from 'scoreboard/datasets/filters/filter-text'
import ifOperators from './if-operators'
import DraggableList from 'draggable-list'
import i18n from 'scoreboard/components/i18n'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import highlight from 'scoreboard/components/highlight'
import reorderAfterDrag from 'scoreboard/components/reorder-after-drag'
import { formatNumericValueForLocalizedDisplay } from './numeric-value-localization'
import unwrapModel from 'scoreboard/components/unwrap-model'

var Transformations = Backbone.View.extend({

	/**
	 * #13966 Step within a wizard which allows specifying transformations to
	 * apply to data before it is imported into Scoreboard. Used by datasets,
	 * metric value imports and initiative status imports.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Importable} options.model
	 *
	 * @param {Wizard} [options.wizard] the current wizard this view is
	 * participating in
	 */
	initialize: function(options = {}) {
		this.options = options
		if (!this.model.get('transformations')) {
			this.model.set('transformations', [])
		}
		this.transformations = this.model.get('transformations')
		this.originalTransformations = _.cloneDeep(this.transformations)
		this._fields = this.fields()
		this._fieldsById = this.fieldsById()
		this.formattingOptionsByFieldRawColumn = this.getFormattingOptionsByFieldRawColumn() || {}
	},

	tagName: 'form',
	className: 'transformations',

	events: {
		'click .add-transformation': 'addTransformation',
		'click .transformation': 'editTransformation',
		'click .delete': 'deleteTransformation'
	},

	// e.g. 'admin.import.transform.banner' indicates our 3 bullets will be
	// 'admin.import.transform.banner.1', 'admin.import.transform.banner.2', etc
	bannerKeyPrefix: null,

	groupBy: null,

	render: function() {
		this.$el.empty().append(template())
		this.renderBannerBullets()

		this.formView = new FormView({
			el: this.el,
			submit: this.submit.bind(this),
			allowCleanSubmission: !_.isNil(this.options.wizard)
		})
		this.options.wizard && this.options.wizard.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})

		this.formView.start()
		// #54506 move renderTransformations() after formView.start() so that
		// the formView can be used to display errors
		this.renderTransformations()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })

		// #31642 display message if database is offline/unavailable
		let messageCode = this.model.getDataSourceMetadata('messageCode')
		if (messageCode) {
			let label = _.get(this.model.attributes, 'connection.label')
			let message = i18n.get(messageCode, label)
			message += ' ' + i18n.get('admin.import.transformations.notAvailable')
			this.formView.errorMessage(message)
			// prevent adding new or modifying existing transformations
			this.$('.add-transformation').addClass('is-not-displayed')
			this.$('li.transformation').removeClass('transformation')
		}

		return this
	},

	renderBannerBullets: function() {
		for (let i=0; i<3; i++) {
			let key = `${this.bannerKeyPrefix}.${i+1}`
			let msg = i18n.get(key)
			if (this.model.get('type') === 'initiative-status') {
				msg = i18n.get(key, highlight(i18n.get(this.bannerHighlightedKeys[i])))
			}
			this.$('.banner li').eq(i).html(msg)
		}
	},

	renderTransformations: function() {
		this.toggleTransformations()

		let html = ''
		_.each(this.transformations, transformation => {
			html += '<li class="transformation"><div>'

			let field = this._fieldsById[transformation.if.fieldId]
			const ifContext = getIfContext(transformation.if, field)
			html += ifDisplay(ifContext)

			let dataType = ifOperators.operatorsToDataTypes[transformation.if.operator]
			let thens = transformation.thens
			_.each(thens, then => {
				let field = this._fieldsById[then.fieldId]
				const operatorKey = enums.thenOperator[then.operator]
				// #40586 if we have a dataType for the field, use that for
				// formatting the value for display
				let fieldDataType = this.getFieldDatatype(field)
				if (!_.isNil(fieldDataType)) {
					dataType = fieldDataType
				}
				// #55064 see if there is a more specific dataType for the field
				// since the fieldDataType may be 'number' (from dataSourceMetadata.fields)
				// but the field may actually have a dataType of 'percent'
				let formattingOptions = this.getFormattingOptionsForField(field)
				if (!_.isNil(formattingOptions?.dataType)) {
					dataType = formattingOptions.dataType
				}
				// #54506 in case the field is not present, also provide a fieldLabel
				// so we can still display the name in the transformation description
				let fieldLabel = then.fieldId
				let thenContext = _.defaults({ field, operatorKey, fieldLabel }, then)
				// #44822 removed unneeded moveDecimal when dataType === 'percent'
				// #36330 for make-x thens, display localized numeric values if needed
				// #40586 using the field's dataType, if available
				if (then.operator === 'make-x') {
					thenContext = _.extend(thenContext,
						{ value: formatNumericValueForLocalizedDisplay(then.value, dataType) })
				}

				html += thenDisplay(thenContext)
			})

			html += '</div>' + transformationButtons()


			html += '</li>'
		})

		this.$('.transformations-list').html(html)

		_.each(this.$('.if-display'), el => {
			this.renderTransformationIf(el, $(el).data('operator'))
		})

		_.each(this.$('.then-display'), el => {
			renderTransformationThen(el, $(el).data('operator'))
		})

		this.draggableList = new DraggableList(this.$('.transformations-list')[0])
		this.draggableList.on('move', (node, newIndex, prevIndex) => {
			reorderAfterDrag(this.transformations, newIndex, prevIndex)
			this.formView.change()
		})
		this.displayFieldNotFoundMessage()
	},

	/**
	 * Returns the dataType for the field, if there is one assigned
	 */
	getFieldDatatype: function(field) {
		return _.get(field, 'dataType')
	},

	/**
	 * Returns a map of if operators to their associated data type
	 */
	getOperatorsToDataTypes: function() {
		return ifOperators.operatorsToDataTypes
	},

	renderTransformationIf: function(el, operator) {
		let operators = _.keys(ifOperators.combinedEnum())
		let selectors = _.map(operators, op => '.' + op)
		// e.g. .is-not-any, .is-any, etc.
		let allSelectors = _.join(selectors, ', ')
		let ifDisplayEl = this.$(el).find('.if-display')
		ifDisplayEl.find(allSelectors).addClass('is-not-displayed')
		ifDisplayEl.find(`.${operator}`).removeClass('is-not-displayed')
	},

	toggleTransformations: function(hasTransformations) {
		hasTransformations = _.isNil(hasTransformations) ?
			!_.isEmpty(this.transformations) : hasTransformations
		this.$('.transformations-list').toggleClass('is-not-displayed', !hasTransformations)
		this.$('.no-transformations').toggleClass('is-not-displayed', hasTransformations)
	},

	/**
	 * #54506 displays an error message if any of the fields used in the
	 * transformation is not found (usually due to changes in the data source)
	 */
	displayFieldNotFoundMessage: function() {
		let missingFields = []
		// check to see if the transformation's if and then fields are present
		_.each(this.transformations, transformation => {
			let ifFieldId = transformation.if.fieldId
			let ifField = this._fieldsById[ifFieldId]
			if (_.isUndefined(ifField)) {
				missingFields.push(ifFieldId)
			}
			let thens = transformation.thens
			_.each(thens, then => {
				let thenFieldId = then.fieldId
				let field = this._fieldsById[thenFieldId]
				if (_.isUndefined(field)) {
					missingFields.push(thenFieldId)
				}
			})
		})
		if (missingFields.length > 0) {
			let messageCode = 'admin.import.transformations.fieldNotFound'
			let uniqFields = _.uniq(missingFields)
			if (uniqFields.length > 1) {
				messageCode = 'admin.import.transformations.fieldNotFound.plural'
			}
			let fields = _.join(uniqFields, ', ')
			let message = i18n.highlight(messageCode, fields)
			this.formView.errorMessage(message)
		}
	},

	/**
	 * Returns the fields which must id and label properties, and may optionally
	 * have a dataTypes property. Must be implemented by subclasses.
	 */
	fields: function() {

	},

	fieldsById: function() {
		return _.keyBy(this.fields(), 'id')
	},

	/**
	 * Returns a map of formatting options for basic fields,
	 * keyed by the rawColumnName that the field's equation references.
	 * Currently, this will only be populated for datasets.
	 * Used for determining the dataTypes to use when formatting
	 * transformed values in the data-preview-panel. Note that this
	 * may return a more specific dataType for a field than
	 * getDataTypesByFieldLabel (which is getting dataTypes from
	 * 'this.model.get('dataSourceMetadata').fields')
	 * (#55064 this function was moved from transformation-form.js
	 * since it is needed throughout for formatting transformed values)
	 */
	getFormattingOptionsByFieldRawColumn: function() {
		let {fields = [], rawColumnsByBasicFieldId = {}} = unwrapModel(this.model)
		fields = _.filter(fields, f => !_.isNil(f.dataType))
		const formattingOptions = new Map(fields.map(field => {
			const {id, dataType, precision, currencyCode} = field
			const rawColumnName = rawColumnsByBasicFieldId[id]
			return [rawColumnName, {dataType, precision, currencyCode}]
		}))
		formattingOptions.delete(null)
		formattingOptions.delete(undefined)
		return formattingOptions
	},

	/**
	 * Returns the formatting options for the given field
	 * or an empty object if none are found
	 */
	getFormattingOptionsForField: function(field) {
		let formattingOptions = {}
		if (!_.isEmpty(this.formattingOptionsByFieldRawColumn)) {
			formattingOptions = this.formattingOptionsByFieldRawColumn.get(field?.label)
		}
		return formattingOptions || {}
	},

	addTransformation: function() {
		let transformationForm = new TransformationForm({
			model: this.model,
			fields: this._fields,
			groupBy: this.groupBy,
			formatValues: _.get(this.options, 'formatValues', true),
			formattingOptionsByFieldRawColumn: this.formattingOptionsByFieldRawColumn
		})
		appDisplay.dialog(transformationForm)
		transformationForm.on('done', transformation => {
			this.transformations.push(transformation)
			this.renderTransformations()
			this.formView.change()
		})
	},

	editTransformation: function(e) {
		let { index } = this.getTransformationElAndIndex(e)
		let transformationForm = new TransformationForm({
			model: this.model,
			fields: this._fields,
			groupBy: this.groupBy,
			transformation: this.transformations[index],
			formatValues: _.get(this.options, 'formatValues', true),
			formattingOptionsByFieldRawColumn: this.formattingOptionsByFieldRawColumn
		})
		appDisplay.dialog(transformationForm)
		transformationForm.on('done', transformation => {
			this.transformations[index] = transformation
			this.renderTransformations()
			this.formView.change()
		})
	},

	deleteTransformation: function(e) {
		e.stopPropagation()
		let { transformationEl, index } = this.getTransformationElAndIndex(e)
		transformationEl.remove()
		this.transformations.splice(index, 1)
		this.toggleTransformations()
		this.formView.change()
		this.displayFieldNotFoundMessage()
	},

	getTransformationElAndIndex: function(e) {
		let transformationEl = this.$(e.target).closest('.transformation')
		let index = transformationEl.index()
		return { transformationEl, index }
	},

	saveState: function() {
		// nothing to do here. we mutated the transformations directly on the
		// model
	},

	url: function() {
		return _.result(this.model, 'url')
	},

	/**
	 * #35006 Populate the field list with `Groups in Regular Expression` values
	 * @param fields
	 */
	addRegexGroupFields(fields) {
		for (let i = 1; i <= 10; i++) {
			fields.push({
				id: `group-${i}-in-regular-expression`,
				label: i18n.get(`admin.import.transform.then.group${i}InRegularExpression`),
				group: 'headerless',
				display: 'thenOnly',
				forOperators: [ 'matches-regular-expression' ],
				sortOrder: i
			})
		}
	},

	submit: function() {
		this.saveState()
		if (this.options.wizard) {
			this.options.wizard.next()
		} else {
			let url = this.url()
			this.model.save({}, {
				url,
				success: () => this.trigger('done', this.model),
				error: (model, response) => this.formView.error(response)
			})
		}
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.formView, this.draggableList, this.covertButtonTooltips], 'remove')
	}

})

const getIfContext = (ifClause, field, opts={}) => {
	let dataType = ifOperators.operatorsToDataTypes[ifClause.operator]
	let operatorKey = ifOperators.combinedEnum()[ifClause.operator]
	// #40586 if we have a dataType for the field, use that for
	// formatting the value for display
	let fieldDataType = _.get(field, 'dataType')
	if (!_.isNil(fieldDataType)) {
		dataType = fieldDataType
	}
	// create a field in the format expected by filter-text.js
	let filterTextField = {
		id: field?.id,
		label: field?.label,
		dataType,
		currencyCode: field?.currencyCode,
		timeDisplayType: field?.timeDisplayType
	}

	let ft = filterText.noOperator(opts.dataset, ifClause, filterTextField,
		opts.treeItemsById, { showCalendarLabel: true })
	// #54506 if the field is not present, provide a fieldLabel so we can still
	// display the name in the transformation description
	let fieldLabel = ifClause.fieldId
	let ifContext = _.defaults({
			field,
			operatorKey,
			// #49656 - must be named `filterText` to match `action-if-display`
			// template context variable
			filterText: ft,
			fieldLabel
		},
		ifClause)

	// If there's no 'filter-group' wrapper aound the filter text, we need to
	// then apply a wrapper in the template via needsFilterHighlight
	if (ifContext.filterText && ifContext.filterText.indexOf('filter-group') < 0) {
		ifContext.needsFilterHighlight = true
	}
	return ifContext
}

export default Transformations
export { getIfContext }

Transformations.__filename__ = 'imports/transform/transformations.js'
